import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartFrame, { CHART_PALETTE, TOOLTIP_STYLE } from './ChartFrame';
import { PMS_LABELS, fmtNumber } from '../derive';
import { EmptyState } from '../states';

export default function CanvasProperties({
  series = [],
  rows = [],
  total = 0,
  rangeLabel = '',
  onSelectPms,
}) {
  const data = rows.map((row) => ({
    id: row.id || row._id,
    label: PMS_LABELS[row.id || row._id] || row.label || row.id || row._id,
    count: row.count || 0,
  }));
  const chartTotal = data.reduce((s, d) => s + d.count, 0);
  const hasPropertyActivity = Array.isArray(series) && series.length > 0;
  const hasPmsBreakdown = data.length > 0;

  return (
    <div className="grid h-full min-h-0 gap-3 laptop:grid-cols-[minmax(0,1.35fr)_minmax(300px,0.95fr)]">
      <ChartFrame
        title="Property acquisitions"
        subtitle={`By day · ${rangeLabel}`}
        right={
          <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">
            {fmtNumber(total)} in range
          </span>
        }
      >
        {hasPropertyActivity ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <defs>
                <linearGradient id="propertyGrowthFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.24} />
                  <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveEnd" />
              <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#0ea5e9"
                strokeWidth={2}
                fill="url(#propertyGrowthFill)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState
            title="No property acquisitions"
            description="Try widening the date range."
          />
        )}
      </ChartFrame>

      <ChartFrame
        title="Properties by PMS"
        subtitle={`${fmtNumber(total || chartTotal)} properties · click a bar for details`}
      >
        {hasPmsBreakdown ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} />
              <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {data.map((d, i) => (
                  <Cell
                    key={d.id}
                    fill={CHART_PALETTE[i % CHART_PALETTE.length]}
                    cursor="pointer"
                    onClick={() => onSelectPms?.(d.id)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState
            title="No PMS distribution"
            description="No properties match the current scope."
          />
        )}
      </ChartFrame>
    </div>
  );
}
